/*
 * A menu as the editor holds it, before it is published.
 *
 * The published shape (label and a resolved href) lives in `public-site.ts`, because that is what a visitor's browser
 * receives. This is the other end: what a staff member is editing, where a link is still "this page of ours" rather
 * than a path, so that renaming a page's address does not silently break the menu.
 *
 * `private.check_navigation_items` in the database is authoritative and raises 23514 on anything it refuses. The
 * checks here exist so the editor can say what is wrong beside the field instead of after a failed save; they are
 * deliberately the same rules, and `menu-draft.test.ts` is where they are kept honest.
 */

import { MENU_ACTIONS, type MenuAction } from "./public-site";

/** One item. It links to a page of ours or to an address — never both, never neither. */
export interface MenuDraftItem {
  readonly label: string;
  readonly pageId?: string | undefined;
  readonly href?: string | undefined;
  readonly children?: readonly MenuDraftItem[] | undefined;
  /** A top-level item shown as one of the site's two buttons (ADR 0073, the section row). */
  readonly action?: MenuAction | undefined;
}

const isAction = (value: unknown): value is MenuAction =>
  typeof value === "string" && (MENU_ACTIONS as readonly string[]).includes(value);

export const MENU_MAX_ITEMS_PER_LEVEL = 12;
export const MENU_MAX_LABEL = 80;
export const MENU_MAX_HREF = 500;

/*
 * The address forms a menu may point at. Anything else is refused, including `javascript:` and bare `http://`.
 *
 * `//evil.test` is refused explicitly: a browser reads a leading `//` as "same scheme, another site", so it is an
 * off-site link wearing the clothes of one of ours. The database refuses it too, since 2026-09-23.
 */
const ADDRESS = /^(\/(?!\/)|https:\/\/|mailto:|tel:)/;

/** A problem with one item, addressed by its position so the editor can show it in the right place. */
export interface MenuProblem {
  /** Indexes from the top of the menu down to the item, e.g. `[2, 0]` is the first child of the third item. */
  readonly path: readonly number[];
  readonly message: string;
}

export interface MenuCheckContext {
  /** The ids of the church's pages that still exist. A link to anything else is a dead link. */
  readonly pageIds: ReadonlySet<string>;
}

function checkLevel(
  items: readonly MenuDraftItem[],
  context: MenuCheckContext,
  depth: number,
  prefix: readonly number[],
  out: MenuProblem[],
): void {
  if (items.length > MENU_MAX_ITEMS_PER_LEVEL) {
    out.push({
      path: prefix,
      message: `A menu level holds at most ${String(MENU_MAX_ITEMS_PER_LEVEL)} items.`,
    });
  }
  items.forEach((item, index) => {
    const path = [...prefix, index];
    const label = item.label.trim();
    if (label.length === 0) {
      out.push({ path, message: "Give this item a label." });
    } else if (label.length > MENU_MAX_LABEL) {
      out.push({ path, message: `A label is at most ${String(MENU_MAX_LABEL)} characters.` });
    }

    const pageId = item.pageId ?? "";
    const hasPage = pageId !== "";
    const href = item.href?.trim() ?? "";
    const hasHref = href.length > 0;
    if (hasPage === hasHref) {
      out.push({ path, message: "Link this item to one of your pages, or to an address." });
    } else if (hasPage) {
      if (!context.pageIds.has(pageId)) {
        out.push({ path, message: "This links to a page that no longer exists." });
      }
    } else {
      if (href.length > MENU_MAX_HREF) {
        out.push({ path, message: `An address is at most ${String(MENU_MAX_HREF)} characters.` });
      } else if (!ADDRESS.test(href)) {
        out.push({ path, message: "An address starts with /, https://, mailto: or tel:." });
      }
    }

    if (item.action !== undefined && depth > 1) {
      out.push({ path, message: "Only a top-level item can be a button." });
    }

    if (item.children !== undefined && item.children.length > 0) {
      if (depth > 1) {
        out.push({ path, message: "Menus go one level deep." });
      } else {
        checkLevel(item.children, context, depth + 1, path, out);
      }
    }
  });
}

/** Every problem with a menu, in the order a reader would meet them. An empty list means the database will accept it. */
export function checkMenuItems(
  items: readonly MenuDraftItem[],
  context: MenuCheckContext,
): MenuProblem[] {
  const out: MenuProblem[] = [];
  checkLevel(items, context, 1, [], out);
  return out;
}

/** The shape `save_navigation` expects: snake_case keys, and no key for the half that does not apply. */
export function toSavedMenuItems(items: readonly MenuDraftItem[]): unknown[] {
  return items.map((item) => {
    const saved: Record<string, unknown> = { label: item.label.trim() };
    if (item.pageId !== undefined && item.pageId !== "") saved.page_id = item.pageId;
    else saved.href = item.href?.trim() ?? "";
    if (item.action !== undefined) saved.action = item.action;
    if (item.children !== undefined && item.children.length > 0) {
      saved.children = toSavedMenuItems(item.children);
    }
    return saved;
  });
}

/** The inverse, for reading a draft back out of the database. Anything malformed is dropped rather than shown wrong. */
export function fromSavedMenuItems(value: unknown): MenuDraftItem[] {
  if (!Array.isArray(value)) return [];
  const out: MenuDraftItem[] = [];
  for (const raw of value) {
    if (typeof raw !== "object" || raw === null) continue;
    const row = raw as Record<string, unknown>;
    if (typeof row.label !== "string") continue;
    const children = fromSavedMenuItems(row.children);
    out.push({
      label: row.label,
      pageId: typeof row.page_id === "string" ? row.page_id : undefined,
      href: typeof row.href === "string" ? row.href : undefined,
      ...(isAction(row.action) ? { action: row.action } : {}),
      ...(children.length > 0 ? { children } : {}),
    });
  }
  return out;
}
